import { useState, useEffect } from "react";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DollarSign, Play, History, Calculator, Mail, Bell } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

function formatKES(num) {
  if (num == null) return "KES 0";
  return `KES ${Number(num).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PayrollPage() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  // Calculator
  const [calcGross, setCalcGross] = useState("");
  const [calcAllowances, setCalcAllowances] = useState("0");
  const [calcHelb, setCalcHelb] = useState("0");
  const [calcCarBenefit, setCalcCarBenefit] = useState("0");
  const [calcMeals, setCalcMeals] = useState("0");
  const [calcOtherBenefits, setCalcOtherBenefits] = useState("0");
  const [calcOtherPension, setCalcOtherPension] = useState("0");
  const [calcPostRetirement, setCalcPostRetirement] = useState("0");
  const [calcResult, setCalcResult] = useState(null);
  const [sendingNotif, setSendingNotif] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    API.get("/payroll/runs").then(({ data }) => setRuns(data)).catch(console.error).finally(() => setLoading(false));
    API.get("/notifications").then(({ data }) => setNotifications(data)).catch(() => {});
  }, []);

  const processPayroll = async () => {
    if (!window.confirm(`Process payroll for ${month}? This will generate payslips for all active employees.`)) return;
    setProcessing(true);
    try {
      const { data } = await API.post("/payroll/process", { month });
      toast.success(data.message);
      API.get("/payroll/runs").then(({ data }) => setRuns(data));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setProcessing(false);
    }
  };

  const sendNotifications = async () => {
    if (!month) return;
    setSendingNotif(true);
    try {
      const { data } = await API.post("/payslips/send-notifications", { month });
      toast.success(data.message);
      API.get("/notifications").then(({ data }) => setNotifications(data));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSendingNotif(false);
    }
  };

  const handleCalculate = async () => {
    try {
      const { data } = await API.post("/payroll/calculate", {
        gross_salary: parseFloat(calcGross) || 0,
        allowances: parseFloat(calcAllowances) || 0,
        helb: parseFloat(calcHelb) || 0,
        car_benefit: parseFloat(calcCarBenefit) || 0,
        meals_benefit: parseFloat(calcMeals) || 0,
        other_benefits: parseFloat(calcOtherBenefits) || 0,
        other_pension: parseFloat(calcOtherPension) || 0,
        post_retirement_medical: parseFloat(calcPostRetirement) || 0,
      });
      setCalcResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>
        Payroll
      </h1>

      <Tabs defaultValue="process" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="process" data-testid="tab-process" className="flex items-center gap-1.5"><Play className="h-3.5 w-3.5" /> Process</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history" className="flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> History</TabsTrigger>
          <TabsTrigger value="calculator" data-testid="tab-calculator" className="flex items-center gap-1.5"><Calculator className="h-3.5 w-3.5" /> Calculator</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications" className="flex items-center gap-1.5"><Bell className="h-3.5 w-3.5" /> Notifications</TabsTrigger>
        </TabsList>

        {/* Process Tab */}
        <TabsContent value="process">
          <div className="bg-white border border-slate-200 rounded-sm p-6 max-w-md">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-sm bg-[#002FA7]/10 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-[#002FA7]" />
              </div>
              <div>
                <h3 className="font-medium text-slate-900">Process Monthly Payroll</h3>
                <p className="text-xs text-slate-500">Generate payslips for all active employees</p>
              </div>
            </div>
            <div className="mb-4">
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Pay Period</label>
              <input
                data-testid="payroll-month-input"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
              />
            </div>
            <button
              data-testid="process-payroll-button"
              onClick={processPayroll}
              disabled={processing}
              className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm w-full"
            >
              {processing ? "Processing..." : "Run Payroll"}
            </button>
            <button
              data-testid="send-notifications-button"
              onClick={sendNotifications}
              disabled={sendingNotif}
              className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-sm px-5 py-2.5 font-medium transition-colors text-sm w-full mt-2 flex items-center justify-center gap-2"
            >
              <Mail className="h-4 w-4" /> {sendingNotif ? "Sending..." : "Email Payslip Notifications"}
            </button>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Month</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right">Employees</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right hidden sm:table-cell">Total Gross</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right hidden md:table-cell">Total Deductions</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right">Total Net</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-slate-500">Loading...</TableCell></TableRow>
                ) : runs.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-slate-500">No payroll runs yet</TableCell></TableRow>
                ) : (
                  runs.map((r) => (
                    <TableRow key={r.month} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`payroll-run-${r.month}`}>
                      <TableCell className="px-4 py-3 font-medium text-sm text-slate-900">{r.month}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-700 text-right">{r.employee_count}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-700 text-right font-mono hidden sm:table-cell">{formatKES(r.total_gross)}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-[#991B1B] text-right font-mono hidden md:table-cell">{formatKES(r.total_deductions)}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-[#065F46] text-right font-mono font-medium">{formatKES(r.total_net)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Calculator Tab */}
        <TabsContent value="calculator">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h3 className="text-lg font-medium text-slate-900 mb-4">Salary Calculator</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Basic Salary (KES)</label>
                    <input data-testid="calc-gross-input" type="number" value={calcGross} onChange={(e) => setCalcGross(e.target.value)}
                      className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm" placeholder="e.g. 100000" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Allowances (KES)</label>
                    <input data-testid="calc-allowances-input" type="number" value={calcAllowances} onChange={(e) => setCalcAllowances(e.target.value)}
                      className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm" placeholder="0" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Car Benefit (KES)</label>
                    <input type="number" value={calcCarBenefit} onChange={(e) => setCalcCarBenefit(e.target.value)}
                      className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm" placeholder="0" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Meals Benefit (KES)</label>
                    <input type="number" value={calcMeals} onChange={(e) => setCalcMeals(e.target.value)}
                      className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm" placeholder="0" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Other Benefits (KES)</label>
                    <input type="number" value={calcOtherBenefits} onChange={(e) => setCalcOtherBenefits(e.target.value)}
                      className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm" placeholder="0" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">HELB Deduction (KES)</label>
                    <input data-testid="calc-helb-input" type="number" value={calcHelb} onChange={(e) => setCalcHelb(e.target.value)}
                      className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm" placeholder="0" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Other Pension (KES)</label>
                    <input type="number" value={calcOtherPension} onChange={(e) => setCalcOtherPension(e.target.value)}
                      className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm" placeholder="0" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Post-Retirement Medical (KES)</label>
                    <input type="number" value={calcPostRetirement} onChange={(e) => setCalcPostRetirement(e.target.value)}
                      className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm" placeholder="0" />
                  </div>
                </div>
                <button data-testid="calculate-button" onClick={handleCalculate}
                  className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm w-full">
                  Calculate
                </button>
              </div>
            </div>

            {calcResult && (
              <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="calc-result">
                <h3 className="text-lg font-medium text-slate-900 mb-4">Payslip Breakdown</h3>
                <div className="space-y-3 text-sm">
                  <Row label="Basic Salary" value={formatKES(calcResult.basic_salary)} />
                  {calcResult.allowances > 0 && <Row label="Allowances" value={formatKES(calcResult.allowances)} />}
                  {calcResult.car_benefit > 0 && <Row label="Car Benefit" value={formatKES(calcResult.car_benefit)} />}
                  {calcResult.meals_benefit > 0 && <Row label="Meals Benefit" value={formatKES(calcResult.meals_benefit)} />}
                  {calcResult.other_benefits > 0 && <Row label="Other Benefits" value={formatKES(calcResult.other_benefits)} />}
                  <Row label="Total Gross Pay (H)" value={formatKES(calcResult.gross_pay)} bold />
                  <div className="border-t border-slate-100 pt-3">
                    <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Statutory Deductions</span>
                  </div>
                  <Row label="NSSF (Tier I)" value={formatKES(calcResult.nssf?.tier1)} red />
                  <Row label="NSSF (Tier II)" value={formatKES(calcResult.nssf?.tier2)} red />
                  <Row label="SHIF/SHA (2.75%)" value={formatKES(calcResult.shif)} red />
                  <Row label="Housing Levy (1.5%)" value={formatKES(calcResult.housing_levy)} red />
                  {calcResult.other_pension > 0 && <Row label="Other Pension" value={formatKES(calcResult.other_pension)} red />}
                  <div className="border-t border-slate-100 pt-3">
                    <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Tax Computation</span>
                  </div>
                  <Row label="SHIF+NSSF Deductible (max 30K)" value={formatKES(calcResult.shif_nssf_deductible)} />
                  {calcResult.pension_deductible > 0 && <Row label="Pension Deductible (max 15K)" value={formatKES(calcResult.pension_deductible)} />}
                  {calcResult.medical_deductible > 0 && <Row label="Medical Fund Deductible (max 30K)" value={formatKES(calcResult.medical_deductible)} />}
                  {calcResult.pwd_exemption > 0 && <Row label="PWD Exemption" value={formatKES(calcResult.pwd_exemption)} green />}
                  <Row label="Taxable Income (O)" value={formatKES(calcResult.taxable_income)} bold />
                  <Row label="Gross Tax" value={formatKES(calcResult.paye?.gross_tax)} red />
                  <Row label="Personal Relief (P)" value={`- ${formatKES(calcResult.paye?.personal_relief)}`} green />
                  <Row label="Net PAYE (S)" value={formatKES(calcResult.paye?.net_paye)} red bold />
                  {calcResult.helb > 0 && <Row label="HELB" value={formatKES(calcResult.helb)} red />}
                  <div className="border-t border-slate-200 pt-3 mt-3">
                    <Row label="Total Deductions" value={formatKES(calcResult.total_deductions)} red bold />
                  </div>
                  <div className="bg-[#ECFDF5] border border-[#10B981] rounded-sm p-3 mt-3">
                    <Row label="Net Pay" value={formatKES(calcResult.net_pay)} green bold />
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-sm p-6 max-w-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-sm bg-[#002FA7]/10 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-[#002FA7]" />
                </div>
                <div>
                  <h3 className="font-medium text-slate-900">Send Payslip Notifications</h3>
                  <p className="text-xs text-slate-500">Email payslip summaries to all employees (mock)</p>
                </div>
              </div>
              <div className="mb-3">
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Month</label>
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                  className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 text-sm" />
              </div>
              <button data-testid="send-notif-tab-btn" onClick={sendNotifications} disabled={sendingNotif}
                className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm w-full">
                {sendingNotif ? "Sending..." : "Send Notifications"}
              </button>
            </div>
            {notifications.length > 0 && (
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Recipient</TableHead>
                      <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden sm:table-cell">Subject</TableHead>
                      <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden md:table-cell">Month</TableHead>
                      <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Status</TableHead>
                      <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden lg:table-cell">Sent At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notifications.slice(0, 50).map((n) => (
                      <TableRow key={n.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <TableCell className="px-4 py-3">
                          <div className="text-sm font-medium text-slate-900">{n.recipient_name}</div>
                          <div className="text-xs text-slate-500">{n.recipient_email}</div>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-slate-700 hidden sm:table-cell">{n.subject}</TableCell>
                        <TableCell className="px-4 py-3 text-sm text-slate-700 hidden md:table-cell">{n.month}</TableCell>
                        <TableCell className="px-4 py-3">
                          <Badge variant="outline" className="text-xs">{n.status}</Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-slate-500 hidden lg:table-cell">{n.sent_at?.split("T")[0]}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Row({ label, value, red, green, bold }) {
  return (
    <div className="flex justify-between items-center">
      <span className={`text-slate-${bold ? "900 font-medium" : "600"}`}>{label}</span>
      <span className={`font-mono ${bold ? "font-medium " : ""}${red ? "text-[#991B1B]" : green ? "text-[#065F46]" : "text-slate-900"}`}>{value}</span>
    </div>
  );
}
